import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { functions } from '../firebase';
import {
  buildDefaultOrderFeeTypes,
  getOrderFeeTypes,
  saveOrderFeeTypes
} from '../firebase/orderFeeTypesSettings';
import {
  getMonthlySalesGoalsForYear,
  getMonthlySalesProgress,
  saveMonthlySalesGoalsForYear
} from '../utils/monthlySalesProgress';
import { runBackfillDesignMockupIndex } from '../utils/backfillDesignMockupIndex';
import { analyzeDuplicateCustomers, mergeDuplicateCustomers } from '../utils/dedupeCustomers';
import { getCustomerPortalUrl } from '../utils/customerPortal';

const ACCENT = 'var(--al-primary)';
const PAGE_BG = 'var(--al-bg)';
const CARD_BG = 'var(--al-surface)';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#98FB98' },
    background: { default: '#1C1C1C', paper: '#2C2C2C' },
    text: { primary: '#fff', secondary: 'rgba(255, 255, 255, 0.7)' }
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif'
  }
});

const Page = styled.div`
  min-height: 100vh;
  background: ${PAGE_BG};
  color: #fff;
  padding: 88px 24px 48px;
`;

const PageInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageHeader = styled.header`
  margin-bottom: 40px;
`;

const PageTitle = styled.h1`
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 600;
  color: ${ACCENT};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 52rem;
`;

const Section = styled.section`
  margin-bottom: 36px;
`;

const SectionLabel = styled.h2`
  margin: 0 0 16px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.5);
`;

const Card = styled.div`
  background: ${CARD_BG};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 20px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${ACCENT};
  line-height: 1.2;
`;

const StatHint = styled.div`
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.45);
`;

const PaymentsLinkCard = styled(Link)`
  display: block;
  margin-bottom: 36px;
  padding: 20px 24px;
  background: ${CARD_BG};
  border: 1px solid rgba(var(--al-primary-rgb), 0.25);
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${ACCENT};
  }
`;

const PaymentsLinkTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${ACCENT};
  margin-bottom: 6px;
`;

const PaymentsLinkText = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);
`;

const GoalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px 16px;
`;

const GoalCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GoalMonth = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FeeTable = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const FeeTableHead = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr 120px 48px;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.25);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.5);

  @media (max-width: 640px) {
    display: none;
  }
`;

const FeeTableRow = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr 120px 48px;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  @media (max-width: 640px) {
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    padding: 14px;
  }
`;

const FeeOrderButtons = styled.div`
  display: flex;
  gap: 2px;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
`;

const StatusText = styled.p`
  margin: 12px 0 0;
  font-size: 0.875rem;
  color: ${(p) => (p.$error ? '#f44336' : ACCENT)};
`;

const ToolsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-child {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const ToolsCopy = styled.div`
  flex: 1;
  min-width: 200px;
`;

const outlineButtonSx = {
  borderColor: ACCENT,
  color: ACCENT,
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { borderColor: ACCENT, backgroundColor: 'rgba(var(--al-primary-rgb), 0.08)' }
};

function formatCurrency(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Strip to digits (and one decimal) for goal input state. */
function parseGoalDigits(raw) {
  const cleaned = String(raw ?? '').replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
}

/** Display goal in inputs: $92,500 */
function formatGoalAmount(value) {
  if (value === '' || value == null) return '';
  const num = Number(parseGoalDigits(value));
  if (!Number.isFinite(num)) return '';
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}

function newFeeTypeRowKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ft-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function withStableFeeTypeRowKeys(types) {
  return (Array.isArray(types) ? types : []).map((t) => ({
    ...t,
    _rowKey: t._rowKey || newFeeTypeRowKey()
  }));
}

function OwnerDashboard() {
  const [metrics, setMetrics] = useState({
    yesterdayPostCOGS: 0,
    mtdInvoiced: 0,
    notInvoicedTotal: 0,
    allCurrent: 0,
    monthlyGoal: 0
  });
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [mockupBackfillRunning, setMockupBackfillRunning] = useState(false);
  const [mockupBackfillResult, setMockupBackfillResult] = useState(null);
  const [mockupBackfillLog, setMockupBackfillLog] = useState([]);
  const [goalGrid, setGoalGrid] = useState({});
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [goalSaveMessage, setGoalSaveMessage] = useState('');
  const [feeTypesEdit, setFeeTypesEdit] = useState([]);
  const [feeTypesLoading, setFeeTypesLoading] = useState(true);
  const [feeTypesPersisted, setFeeTypesPersisted] = useState(false);
  const [feeTypesSaving, setFeeTypesSaving] = useState(false);
  const [feeTypesSaveMessage, setFeeTypesSaveMessage] = useState('');
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [focusedGoalMonth, setFocusedGoalMonth] = useState(null);
  const [dedupeEmail, setDedupeEmail] = useState('ben@eightsevencentral.com');
  const [dedupePreferClient, setDedupePreferClient] = useState('Ben Anderson');
  const [dedupeAnalysis, setDedupeAnalysis] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupeMerging, setDedupeMerging] = useState(false);
  const [dedupeMessage, setDedupeMessage] = useState('');
  const [dedupePortalUrl, setDedupePortalUrl] = useState('');

  const currentYear = new Date().getFullYear();
  const currentMonthLabel = new Date().toLocaleString('default', { month: 'long' });

  const monthRows = useMemo(
    () => Array.from({ length: 12 }, (_, idx) => {
      const monthIndex = idx + 1;
      const monthKey = `${currentYear}-${String(monthIndex).padStart(2, '0')}`;
      return {
        monthKey,
        label: new Date(currentYear, idx, 1).toLocaleString('default', { month: 'short' })
      };
    }),
    [currentYear]
  );

  const goalProgressPct = useMemo(() => {
    const goal = Number(metrics.monthlyGoal || 0);
    if (goal <= 0) return 0;
    return Math.min(100, Math.round((metrics.mtdInvoiced / goal) * 100));
  }, [metrics.mtdInvoiced, metrics.monthlyGoal]);

  const handleSendTestEmail = async () => {
    setTestEmailResult(null);
    setTestEmailSending(true);
    try {
      const sendTestEmail = httpsCallable(functions, 'sendTestEmail');
      const result = await sendTestEmail();
      setTestEmailResult({ ok: true, message: result.data?.message || 'Sent.' });
    } catch (err) {
      setTestEmailResult({ ok: false, message: err.message || 'Failed to send.' });
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleMockupBackfill = async (dryRun) => {
    setMockupBackfillResult(null);
    setMockupBackfillLog([]);
    setMockupBackfillRunning(true);

    try {
      const summary = await runBackfillDesignMockupIndex({
        dryRun,
        onProgress: (message) => {
          setMockupBackfillLog((prev) => [...prev, message]);
        }
      });

      setMockupBackfillResult({
        ok: true,
        dryRun,
        summary
      });
    } catch (err) {
      console.error(err);
      setMockupBackfillResult({
        ok: false,
        message: err.message || 'Backfill failed.'
      });
    } finally {
      setMockupBackfillRunning(false);
    }
  };

  const calculateYesterdayPostCOGS = async () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    const linesRef = collection(db, 'lines');
    const q = query(
      linesRef,
      where('printedAt', '>=', Timestamp.fromDate(yesterday)),
      where('printedAt', '<=', Timestamp.fromDate(yesterdayEnd))
    );

    try {
      const querySnapshot = await getDocs(q);
      let totalPostCOGS = 0;
      querySnapshot.forEach((docSnap) => {
        const line = docSnap.data();
        const revenue = line.total || 0;
        const cogs = line.materialCost || 0;
        totalPostCOGS += revenue - cogs;
      });
      return totalPostCOGS;
    } catch (error) {
      console.error('Error calculating yesterday post-COGS:', error);
      return 0;
    }
  };

  useEffect(() => {
    const loadMetrics = async () => {
      setMetricsLoading(true);
      try {
        const [postCOGS, salesProgress, goalsForYear] = await Promise.all([
          calculateYesterdayPostCOGS(),
          getMonthlySalesProgress(db),
          getMonthlySalesGoalsForYear(db, currentYear)
        ]);

        setMetrics({
          yesterdayPostCOGS: postCOGS,
          mtdInvoiced: salesProgress.mtdInvoiced,
          notInvoicedTotal: salesProgress.notInvoicedTotal,
          allCurrent: salesProgress.allCurrent,
          monthlyGoal: salesProgress.goal
        });
        setGoalGrid(
          Object.fromEntries(
            Object.entries(goalsForYear).map(([key, value]) => {
              const num = Number(value);
              return [key, Number.isFinite(num) && num > 0 ? String(num) : ''];
            })
          )
        );
      } catch (error) {
        console.error('Error loading owner metrics:', error);
      } finally {
        setMetricsLoading(false);
      }
    };

    loadMetrics();
  }, [currentYear]);

  const loadFeeTypes = async () => {
    setFeeTypesLoading(true);
    setFeeTypesSaveMessage('');
    try {
      const { types, fromFirestore } = await getOrderFeeTypes(db);
      setFeeTypesEdit(withStableFeeTypeRowKeys(types.map((t) => ({ ...t }))));
      setFeeTypesPersisted(fromFirestore);
    } catch (e) {
      console.error(e);
      setFeeTypesSaveMessage('Could not load fee types.');
    } finally {
      setFeeTypesLoading(false);
    }
  };

  useEffect(() => {
    loadFeeTypes();
  }, []);

  const moveFeeType = (index, direction) => {
    setFeeTypesEdit((prev) => {
      const j = index + direction;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next.map((t, idx) => ({ ...t, sortOrder: idx }));
    });
  };

  const updateFeeLabel = (index, label) => {
    setFeeTypesEdit((prev) => prev.map((t, i) => (i === index ? { ...t, label } : t)));
  };

  const updateFeeTaxable = (index, taxable) => {
    setFeeTypesEdit((prev) => prev.map((t, i) => (i === index ? { ...t, taxable } : t)));
  };

  const removeFeeType = (index) => {
    setFeeTypesEdit((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((_, i) => i !== index)
        .map((t, idx) => ({ ...t, sortOrder: idx }));
    });
  };

  const addFeeType = () => {
    setFeeTypesEdit((prev) => [
      ...prev,
      { label: '', sortOrder: prev.length, taxable: true, _rowKey: newFeeTypeRowKey() }
    ]);
  };

  const handleResetFeeTypesDefaults = () => {
    setFeeTypesEdit(withStableFeeTypeRowKeys(buildDefaultOrderFeeTypes().map((t) => ({ ...t }))));
    setFeeTypesSaveMessage('Restored default labels (not saved yet).');
  };

  const handleSaveFeeTypes = async () => {
    setFeeTypesSaving(true);
    setFeeTypesSaveMessage('');
    try {
      await saveOrderFeeTypes(db, feeTypesEdit);
      const { types, fromFirestore } = await getOrderFeeTypes(db);
      setFeeTypesEdit(withStableFeeTypeRowKeys(types.map((t) => ({ ...t }))));
      setFeeTypesPersisted(fromFirestore);
      setFeeTypesSaveMessage('Saved fee types.');
    } catch (e) {
      console.error(e);
      setFeeTypesSaveMessage(e?.message || 'Could not save fee types.');
    } finally {
      setFeeTypesSaving(false);
    }
  };

  const handleSaveGoalGrid = async () => {
    setGoalSaveMessage('');
    setIsSavingGoal(true);
    try {
      const savedGoals = await saveMonthlySalesGoalsForYear(db, goalGrid);
      setGoalGrid(
        Object.fromEntries(
          Object.entries(savedGoals).map(([key, value]) => {
            const num = Number(value);
            return [key, Number.isFinite(num) && num > 0 ? String(num) : ''];
          })
        )
      );
      const currentMonthKey = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      setMetrics((prev) => ({ ...prev, monthlyGoal: Number(savedGoals[currentMonthKey] || 0) }));
      setGoalSaveMessage('Monthly goals saved.');
    } catch (error) {
      console.error('Error saving monthly sales goal:', error);
      setGoalSaveMessage('Could not save goals right now.');
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleAnalyzeDedupe = async () => {
    setDedupeMessage('');
    setDedupePortalUrl('');
    setDedupeLoading(true);
    try {
      const result = await analyzeDuplicateCustomers(db, dedupeEmail, {
        preferClient: dedupePreferClient
      });
      setDedupeAnalysis(result);
      if (result.matches.length === 0) {
        setDedupeMessage('No customers found for that email.');
      } else if (result.losers.length === 0) {
        setDedupeMessage('Only one customer — nothing to merge.');
        const url = await getCustomerPortalUrl(db, result.winner);
        setDedupePortalUrl(url);
      }
    } catch (error) {
      console.error(error);
      setDedupeMessage(error.message || 'Scan failed.');
      setDedupeAnalysis(null);
    } finally {
      setDedupeLoading(false);
    }
  };

  const handleMergeDedupe = async () => {
    if (!dedupeAnalysis?.losers?.length) return;
    const names = dedupeAnalysis.losers.map((l) => l.client || l.id).join(', ');
    if (
      !window.confirm(
        `Merge ${dedupeAnalysis.losers.length} duplicate(s) into "${dedupeAnalysis.winner.client}"?\n\nWill delete: ${names}\n\nOrders will move to the kept account.`
      )
    ) {
      return;
    }

    setDedupeMerging(true);
    setDedupeMessage('');
    try {
      const result = await mergeDuplicateCustomers(db, dedupeEmail, {
        preferClient: dedupePreferClient
      });
      setDedupeMessage(
        `Merged. Moved ${result.ordersMoved} order(s), ${result.mockupsMoved} mockup row(s). Deleted ${result.deleted.length} customer(s).`
      );
      const url = await getCustomerPortalUrl(db, result.winner);
      setDedupePortalUrl(url);
      setDedupeAnalysis(null);
    } catch (error) {
      console.error(error);
      setDedupeMessage(error.message || 'Merge failed.');
    } finally {
      setDedupeMerging(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Page>
        <PageInner>
          <PageHeader>
            <PageTitle>Owner dashboard</PageTitle>
            <PageSubtitle>
              Sales targets, fee labels for orders and quotes, and a quick SendGrid check.
            </PageSubtitle>
          </PageHeader>

          <PaymentsLinkCard to="/owner/payments">
            <PaymentsLinkTitle>Payments →</PaymentsLinkTitle>
            <PaymentsLinkText>
              Outstanding invoices, paid history, and mark check/cash payments.
            </PaymentsLinkText>
          </PaymentsLinkCard>

          <Section>
            <SectionLabel>Customer portal — dedupe by email</SectionLabel>
            <Card>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2 }}>
                Find duplicate customer records with the same email, keep the one with the most orders,
                move all orders to it, and delete the extras. Use before testing portal links.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                  label="Email"
                  size="small"
                  value={dedupeEmail}
                  onChange={(e) => setDedupeEmail(e.target.value)}
                  sx={{ minWidth: 280 }}
                />
                <TextField
                  label="Prefer client name"
                  size="small"
                  value={dedupePreferClient}
                  onChange={(e) => setDedupePreferClient(e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAnalyzeDedupe}
                  disabled={dedupeLoading || dedupeMerging}
                  sx={{ color: ACCENT, borderColor: ACCENT }}
                >
                  {dedupeLoading ? 'Scanning…' : 'Scan duplicates'}
                </Button>
                {dedupeAnalysis?.losers?.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleMergeDedupe}
                    disabled={dedupeMerging}
                    sx={{ bgcolor: ACCENT, color: 'var(--al-bg)' }}
                  >
                    {dedupeMerging ? 'Merging…' : 'Merge into winner'}
                  </Button>
                )}
              </Box>

              {dedupeAnalysis?.matches?.length > 0 && (
                <Box sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {dedupeAnalysis.matches.map((c) => (
                    <Box key={c.id} sx={{ py: 0.5, color: 'rgba(255,255,255,0.85)' }}>
                      {c.id === dedupeAnalysis.winner?.id ? '→ KEEP ' : '  drop  '}
                      {c.client || '(no client)'} / {c.contact || '(no contact)'} — {c.email || '(no email)'} —{' '}
                      {c.orderCount} orders — token {c.portalToken ? `${c.portalToken.slice(0, 8)}…` : 'none'}
                    </Box>
                  ))}
                </Box>
              )}

              {dedupeMessage && (
                <Typography variant="body2" sx={{ color: ACCENT, mb: 1 }}>
                  {dedupeMessage}
                </Typography>
              )}
              {dedupePortalUrl && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', wordBreak: 'break-all' }}>
                  Portal link: {dedupePortalUrl}
                </Typography>
              )}
            </Card>
          </Section>

          <Section>
            <SectionLabel>Sales overview</SectionLabel>
            <Card>
              {metricsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: ACCENT }} />
                </Box>
              ) : (
                <>
                  <StatGrid>
                    <StatCard>
                      <StatLabel>{currentMonthLabel} invoiced</StatLabel>
                      <StatValue>{formatCurrency(metrics.mtdInvoiced)}</StatValue>
                      {Number(metrics.monthlyGoal) > 0 && (
                        <StatHint>
                          {goalProgressPct}% of {formatCurrency(metrics.monthlyGoal)} goal
                        </StatHint>
                      )}
                    </StatCard>
                    <StatCard>
                      <StatLabel>Work in progress</StatLabel>
                      <StatValue>{formatCurrency(metrics.notInvoicedTotal)}</StatValue>
                      <StatHint>Active orders not yet invoiced</StatHint>
                    </StatCard>
                    <StatCard>
                      <StatLabel>All current</StatLabel>
                      <StatValue>{formatCurrency(metrics.allCurrent)}</StatValue>
                      <StatHint>Invoiced this month + WIP</StatHint>
                    </StatCard>
                    <StatCard>
                      <StatLabel>Yesterday post-COGS</StatLabel>
                      <StatValue>{formatCurrency(metrics.yesterdayPostCOGS)}</StatValue>
                      <StatHint>From printed lines (legacy collection)</StatHint>
                    </StatCard>
                  </StatGrid>
                  {Number(metrics.monthlyGoal) > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {currentMonthLabel} goal progress
                        </Typography>
                        <Typography variant="caption" sx={{ color: ACCENT }}>
                          {formatCurrency(metrics.mtdInvoiced)} / {formatCurrency(metrics.monthlyGoal)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={goalProgressPct}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { backgroundColor: ACCENT, borderRadius: 4 }
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Card>
          </Section>

          <Section>
            <SectionLabel>{currentYear} monthly goals</SectionLabel>
            <Card>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2 }}>
                Set a sales target for each month. The current month goal drives progress on the welcome screen and above.
              </Typography>
              <GoalsGrid>
                {monthRows.map((row) => (
                  <GoalCell key={row.monthKey}>
                    <GoalMonth>{row.label}</GoalMonth>
                    <TextField
                      value={
                        focusedGoalMonth === row.monthKey
                          ? (goalGrid[row.monthKey] ?? '')
                          : formatGoalAmount(goalGrid[row.monthKey])
                      }
                      onChange={(e) =>
                        setGoalGrid((prev) => ({
                          ...prev,
                          [row.monthKey]: parseGoalDigits(e.target.value)
                        }))
                      }
                      onFocus={() => setFocusedGoalMonth(row.monthKey)}
                      onBlur={() => {
                        setFocusedGoalMonth((current) => (current === row.monthKey ? null : current));
                        setGoalGrid((prev) => {
                          const raw = prev[row.monthKey];
                          if (raw === '' || raw == null) return { ...prev, [row.monthKey]: '' };
                          const num = Math.max(0, Number(parseGoalDigits(raw)) || 0);
                          return { ...prev, [row.monthKey]: num > 0 ? String(num) : '' };
                        });
                      }}
                      size="small"
                      placeholder="$0"
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }
                        }
                      }}
                    />
                  </GoalCell>
                ))}
              </GoalsGrid>
              <ActionRow>
                <Button
                  variant="outlined"
                  onClick={handleSaveGoalGrid}
                  disabled={isSavingGoal}
                  sx={outlineButtonSx}
                >
                  {isSavingGoal ? 'Saving…' : 'Save goals'}
                </Button>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  {currentMonthLabel} target: {formatCurrency(metrics.monthlyGoal)}
                </Typography>
              </ActionRow>
              {goalSaveMessage && (
                <StatusText $error={goalSaveMessage.includes('Could not')}>{goalSaveMessage}</StatusText>
              )}
            </Card>
          </Section>

          <Section>
            <SectionLabel>Order fee types</SectionLabel>
            <Card>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2 }}>
                Labels in the fees dropdown on new orders, quotes, and edits. Shipping labels always map to
                &quot;Shipping&quot;. Sales tax controls whether each fee counts toward the 7% invoice tax base.
              </Typography>
              {feeTypesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} sx={{ color: ACCENT }} />
                </Box>
              ) : (
                <>
                  {!feeTypesPersisted && (
                    <Typography variant="body2" sx={{ color: '#ffb74d', mb: 2 }}>
                      No saved list in Firebase yet — showing defaults. Save to persist your list.
                    </Typography>
                  )}
                  <FeeTable>
                    <FeeTableHead>
                      <span>Order</span>
                      <span>Label</span>
                      <span>Tax</span>
                      <span />
                    </FeeTableHead>
                    {feeTypesEdit.map((row, index) => (
                      <FeeTableRow key={row._rowKey ?? `fee-type-fallback-${index}`}>
                        <FeeOrderButtons>
                          <IconButton
                            size="small"
                            onClick={() => moveFeeType(index, -1)}
                            disabled={index === 0}
                            aria-label="Move up"
                            sx={{ color: ACCENT }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveFeeType(index, 1)}
                            disabled={index === feeTypesEdit.length - 1}
                            aria-label="Move down"
                            sx={{ color: ACCENT }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </FeeOrderButtons>
                        <TextField
                          size="small"
                          placeholder="Fee label"
                          value={row.label}
                          onChange={(e) => updateFeeLabel(index, e.target.value)}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(0,0,0,0.15)',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' }
                            }
                          }}
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={row.taxable !== false}
                              onChange={(e) => updateFeeTaxable(index, e.target.checked)}
                              sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                            />
                          )}
                          label="Taxable"
                          sx={{ color: 'rgba(255,255,255,0.8)', m: 0 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeFeeType(index)}
                          disabled={feeTypesEdit.length <= 1}
                          aria-label="Remove"
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </FeeTableRow>
                    ))}
                  </FeeTable>
                  <ActionRow>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addFeeType} sx={outlineButtonSx}>
                      Add type
                    </Button>
                    <Button variant="outlined" onClick={handleSaveFeeTypes} disabled={feeTypesSaving} sx={outlineButtonSx}>
                      {feeTypesSaving ? 'Saving…' : 'Save fee types'}
                    </Button>
                    <Button variant="text" onClick={handleResetFeeTypesDefaults} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Reset defaults
                    </Button>
                    <Button variant="text" onClick={loadFeeTypes} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Reload
                    </Button>
                  </ActionRow>
                  {feeTypesSaveMessage && (
                    <StatusText
                      $error={
                        feeTypesSaveMessage.includes('Could not') || feeTypesSaveMessage.includes('not save')
                      }
                    >
                      {feeTypesSaveMessage}
                    </StatusText>
                  )}
                </>
              )}
            </Card>
          </Section>

          <Section>
            <SectionLabel>Tools</SectionLabel>
            <Card>
              <ToolsRow>
                <ToolsCopy>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EmailOutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
                      SendGrid test
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    Sends a test message to ben@eightsevencentral.com to confirm email delivery is working.
                  </Typography>
                  {testEmailResult && (
                    <StatusText $error={!testEmailResult.ok}>
                      {testEmailResult.ok ? `✓ ${testEmailResult.message}` : `✗ ${testEmailResult.message}`}
                    </StatusText>
                  )}
                </ToolsCopy>
                <Button
                  variant="outlined"
                  onClick={handleSendTestEmail}
                  disabled={testEmailSending}
                  sx={outlineButtonSx}
                >
                  {testEmailSending ? 'Sending…' : 'Send test email'}
                </Button>
              </ToolsRow>

              <ToolsRow>
                <ToolsCopy>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ImageOutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
                      Mockup suggestion index
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 1 }}>
                    One-time scan of past order lines to build the mockup suggestion library.
                    Preview first, then run once after deploy. New uploads keep the index updated automatically.
                  </Typography>
                  {mockupBackfillResult?.ok && mockupBackfillResult.summary && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 1 }}>
                      Scanned {mockupBackfillResult.summary.scanned} lines ·{' '}
                      {mockupBackfillResult.summary.withMockup} with mockups ·{' '}
                      {mockupBackfillResult.summary.indexCount} index entries
                      {mockupBackfillResult.summary.written
                        ? ` · wrote ${mockupBackfillResult.summary.written}`
                        : mockupBackfillResult.dryRun
                          ? ' (preview only)'
                          : ''}
                    </Typography>
                  )}
                  {mockupBackfillResult && !mockupBackfillResult.ok && (
                    <StatusText $error>✗ {mockupBackfillResult.message}</StatusText>
                  )}
                  {mockupBackfillResult?.ok && (
                    <StatusText>
                      ✓ {mockupBackfillResult.dryRun ? 'Preview complete — no changes made.' : 'Backfill complete.'}
                    </StatusText>
                  )}
                  {mockupBackfillLog.length > 0 && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        backgroundColor: 'rgba(0,0,0,0.25)',
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        color: 'rgba(255,255,255,0.7)',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 120,
                        overflow: 'auto'
                      }}
                    >
                      {mockupBackfillLog.join('\n')}
                    </Box>
                  )}
                </ToolsCopy>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 160 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleMockupBackfill(true)}
                    disabled={mockupBackfillRunning}
                    sx={outlineButtonSx}
                  >
                    {mockupBackfillRunning ? 'Working…' : 'Preview backfill'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleMockupBackfill(false)}
                    disabled={mockupBackfillRunning}
                    sx={{
                      backgroundColor: ACCENT,
                      color: '#222',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#7FFF7F' }
                    }}
                  >
                    {mockupBackfillRunning ? 'Working…' : 'Run backfill'}
                  </Button>
                </Box>
              </ToolsRow>
            </Card>
          </Section>
        </PageInner>
      </Page>
    </ThemeProvider>
  );
}

export default OwnerDashboard;
